/**
 * Supabase client configuration
 * Provides a singleton Supabase client instance for database operations
 */

import { createClient, SupabaseClient } from '@supabase/supabase-js'
import type { Database } from './types/database'

let supabaseClient: SupabaseClient<Database> | null = null

/**
 * Gets the Supabase URL from environment variables
 * In Vite, environment variables are accessed via import.meta.env
 */
function getSupabaseUrl(): string {
  const url = (import.meta as any).env?.VITE_SUPABASE_URL

  if (!url) {
    throw new Error(
      'Supabase URL is not configured. Please set VITE_SUPABASE_URL in your .env file.'
    )
  }

  return url
}

/**
 * Gets the Supabase anon key from environment variables
 * In Vite, environment variables are accessed via import.meta.env
 */
function getSupabaseAnonKey(): string {
  const key = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY

  if (!key) {
    throw new Error(
      'Supabase anon key is not configured. Please set VITE_SUPABASE_ANON_KEY in your .env file.'
    )
  }

  return key
}

/**
 * Creates and returns a Supabase client instance
 * Uses singleton pattern to ensure only one client is created
 */
export function getSupabaseClient(): SupabaseClient<Database> {
  if (!supabaseClient) {
    try {
      const supabaseUrl = getSupabaseUrl()
      const supabaseAnonKey = getSupabaseAnonKey()

      // Validate URL format
      if (!supabaseUrl.startsWith('http://') && !supabaseUrl.startsWith('https://')) {
        throw new Error(`Invalid Supabase URL format: ${supabaseUrl}. URL must start with http:// or https://`)
      }

      // Validate key format (JWT tokens are typically long)
      if (!supabaseAnonKey || supabaseAnonKey.length < 50) {
        throw new Error('Invalid Supabase anon key format. Key appears to be too short or missing.')
      }

      supabaseClient = createClient<Database>(supabaseUrl, supabaseAnonKey, {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          detectSessionInUrl: true
        },
        db: {
          schema: 'public'
        },
        global: {
          headers: {
            'x-client-info': 'bizora-platform'
          }
        }
      })

      if (import.meta.env.DEV) {
        console.log('[Supabase] Client initialized successfully')
        console.log('[Supabase] URL:', supabaseUrl)
        console.log('[Supabase] Key:', supabaseAnonKey.substring(0, 20) + '...')
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      console.error('[Supabase] Failed to create client:', errorMessage)
      throw error
    }
  }

  return supabaseClient
}

/**
 * Resets the Supabase client (useful for testing or re-initialization)
 */
export function resetSupabaseClient(): void {
  supabaseClient = null
}

